<template>
  <div class="awards-table">
    <app-loading :loading="loading" />
    <template v-if="!loading">
      <b-table
        v-if="programs.length > 0"
        :items="enrichedPrograms"
        :fields="AWARDS_TABLE_FIELDS"
        hover
        responsive
        borderless
        show-empty
        class="awards-table__table"
      >
        <template #cell(is_active)="{ item }">
          <div class="awards-table__cell">
            <b-form-checkbox :checked="item.is_active" switch size="lg" class="awards-table__toggle" disabled />
          </div>
        </template>

        <template #cell(program_name)="{ item }">
          <div class="awards-table__cell awards-table__cell--name">
            <div class="awards-table__badge-icon">
              <img
                v-if="item.badge_presigned_url"
                :src="item.badge_presigned_url"
                :alt="item.program_name"
                class="awards-table__badge-image"
              />
              <font-awesome-icon v-else :icon="['fad', 'award']" class="awards-table__badge-placeholder" />
            </div>
            <span class="awards-table__name">{{ item.program_name }}</span>
          </div>
        </template>

        <template #cell(scope)="{ item }">
          <div class="awards-table__cell">
            <template v-if="!item.enabled_divisions || item.enabled_divisions.length === 0">
              <b-badge pill variant="light" class="awards-table__scope-badge">entire company</b-badge>
            </template>
            <template v-else>
              <span :id="`scope-popover-${item.recognition_award_program_id}`" class="awards-table__scope-trigger">
                <b-badge pill variant="light" class="awards-table__scope-badge">
                  {{ item.enabled_divisions.length }}
                  {{ item.enabled_divisions.length === 1 ? 'division' : 'divisions' }}
                </b-badge>
              </span>
              <b-popover
                v-if="item.divisionNames && item.divisionNames.length > 0"
                :target="`scope-popover-${item.recognition_award_program_id}`"
                triggers="hover focus"
                placement="bottom"
              >
                <div v-for="name in item.divisionNames" :key="name" class="awards-table__popover-item">
                  {{ name }}
                </div>
              </b-popover>
            </template>
          </div>
        </template>

        <template #cell(nominators)="{ item }">
          <div class="awards-table__cell">
            <template v-if="item.nominatorsCount > 0">
              <b-badge pill variant="light" class="awards-table__count-badge">
                {{ item.nominatorsCount }} {{ item.nominatorsCount === 1 ? 'nominator' : 'nominators' }}
              </b-badge>
            </template>
            <template v-else>
              <span class="text-muted">-</span>
            </template>
          </div>
        </template>

        <template #cell(approvers)="{ item }">
          <div class="awards-table__cell">
            <template v-if="item.approversCount > 0">
              <b-badge pill variant="light" class="awards-table__count-badge">
                {{ item.approversCount }} {{ item.approversCount === 1 ? 'approver' : 'approvers' }}
              </b-badge>
            </template>
            <template v-else>
              <span class="text-muted">-</span>
            </template>
          </div>
        </template>

        <template #cell(budget)="{ item }">
          <div class="awards-table__cell awards-table__cell--budget">
            <template v-if="item.budget_minor">
              <div class="awards-table__budget-text">
                {{ formatBudget(item.budget_minor) }}
              </div>
            </template>
            <template v-else>
              <span class="text-muted">-</span>
            </template>
          </div>
        </template>

        <template #cell(budget_period)="{ item }">
          <div class="awards-table__cell">
            <template v-if="item.budgetPeriod">
              <span class="text-muted">({{ formatBudgetPeriod(item.budgetPeriod) }})</span>
            </template>
            <template v-else>
              <span class="text-muted"></span>
            </template>
          </div>
        </template>

        <template #cell(actions)="{ item }">
          <div class="awards-table__cell awards-table__cell--actions">
            <router-link
              :to="{ name: 'recognition-award-detail', params: { programId: item.recognition_award_program_id } }"
              class="awards-table__action-arrow"
            >
              <font-awesome-icon :icon="['fal', 'chevron-right']" />
            </router-link>
          </div>
        </template>
      </b-table>

      <div v-else class="awards-table__empty">
        <p class="text-muted">No awards found.</p>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import AppLoading from '@/components/AppLoading.vue';
import { toCurrency } from '@/utils';
import State from '@/state/State';

const props = defineProps({
  programs: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const AWARDS_TABLE_FIELDS = [
  {
    key: 'is_active',
    label: 'Active',
    thStyle: { width: '80px' },
  },
  {
    key: 'program_name',
    label: 'Award name',
  },
  {
    key: 'scope',
    label: 'Scope',
  },
  {
    key: 'nominators',
    label: 'Nominators',
  },
  {
    key: 'approvers',
    label: 'Approvers',
  },
  {
    key: 'budget',
    label: 'Budget',
  },
  {
    key: 'budget_period',
    label: '',
    thStyle: { width: '100px' },
  },
  {
    key: 'actions',
    label: '',
    thStyle: { width: '50px' },
  },
];

const currency = computed(() => State.state?.company?.default_currency);
const divisions = computed(() => State.state?.divisions || []);

// Create a map of division_id to division name
const divisionMap = computed(() => {
  const map = {};
  for (const div of divisions.value) {
    map[div.division_id] = div.name;
  }
  return map;
});

// Enrich programs with computed properties from existing data
const enrichedPrograms = computed(() => {
  return props.programs.map(program => {
    const properties = program.properties || {};

    // Calculate nominators count
    const nominators = properties.nominators || [];
    const nominatorsCount = nominators.length;

    // Calculate approvers count (sum of all approvers across all groups)
    const approverGroups = properties.approvers || [];
    const approversCount = approverGroups.reduce((acc, group) => {
      return acc + (group.approvers?.length || 0);
    }, 0);

    // Map division IDs to names
    const enabledDivisions = program.enabled_divisions || [];
    const divisionNames = enabledDivisions.map(divId => divisionMap.value[divId]).filter(name => name);

    // Get budget period from properties
    const budgetPeriod = properties.budget_period;

    return {
      ...program,
      nominatorsCount,
      approversCount,
      divisionNames,
      budgetPeriod,
    };
  });
});

const formatBudget = value => {
  if (!value) {
    return '-';
  }
  return toCurrency(value, currency.value).replace(/\.00$/, '');
};

const formatBudgetPeriod = period => {
  const periodMap = {
    week: 'weekly',
    month: 'monthly',
    quarter: 'quarterly',
    year: 'yearly',
  };
  return periodMap[period?.toLowerCase()] || period;
};
</script>

<style scoped lang="scss">
.awards-table {
  &__table {
    :deep(thead th) {
      font-weight: 500;
      font-size: 0.875rem;
      color: var(--palette-color-default-lighten-40);
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
      padding: 0.75rem 1rem;
    }

    :deep(tbody td) {
      padding: 1rem;
      vertical-align: middle;
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
    }

    :deep(tbody tr:hover) {
      background-color: var(--palette-color-default-lighten-95);
    }
  }

  &__cell {
    display: flex;
    align-items: center;

    &--name {
      gap: 0.75rem;
    }

    &--budget {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.25rem;
    }

    &--actions {
      justify-content: flex-end;
    }
  }

  &__toggle {
    :deep(.custom-control-label::before),
    :deep(.custom-control-label::after) {
      width: 2.5rem;
      height: 1.25rem;
    }

    :deep(.custom-control-input:checked ~ .custom-control-label::before) {
      background-color: var(--palette-color-brand-primary);
      border-color: var(--palette-color-brand-primary);
    }
  }

  &__badge-icon {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    background-color: var(--palette-color-core-1-lighten-80);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__badge-image {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
  }

  &__badge-placeholder {
    color: var(--palette-color-brand-primary);
    font-size: 1.25rem;
  }

  &__name {
    font-weight: 500;
  }

  &__scope-badge,
  &__count-badge {
    background-color: var(--palette-color-default-lighten-90);
    color: var(--palette-color-default);
    font-weight: 400;
    padding: 0.375rem 0.75rem;
    cursor: default;
  }

  &__scope-trigger {
    cursor: pointer;
  }

  &__popover-item {
    padding: 0.25rem 0;
  }

  &__budget-text {
    font-weight: 500;
  }

  &__budget-progress {
    width: 100%;
    min-width: 80px;
    border-radius: 2px;
  }

  &__action-arrow {
    color: var(--palette-color-default-lighten-40);
    padding: 0.5rem;

    &:hover {
      color: var(--palette-color-brand-primary);
    }
  }

  &__empty {
    padding: 2rem;
    text-align: center;
  }
}
</style>
